import i18n from '../../../../apps/backoffice/i18n'
import { CurrencyEnum } from '../../../../apps/backoffice/src/lib/types/enums/common/currency.enum'

export abstract class NumberFormatter {
  /**
   * Format a number to a string with a thousands separator
   * @param value The number to format
   * @returns The formatted number as a string
   * @example
   * ```ts
   * const value = 1000000;
   * const formattedValue = NumberFormatter.numberFormatter(value);
   * formattedValue // "1,000,000"
   * ```
   */
  static numberFormatter(value: number): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  /**
   *  Round a number to two decimal places
   * @param value  The number to round
   * @returns   The rounded number as a string
   * @example
   * ```ts
   * const value = 1.2345;
   * const roundedValue = NumberFormatter.roundToTwoDecimalPlaces(value);
   * roundedValue // "1.23"
   * ```
   * @example
   * ```ts
   * const value = 1;
   * const roundedValue = NumberFormatter.roundToTwoDecimalPlaces(value);
   * roundedValue // "1"
   * ```
   */

  static roundToTwoDecimalPlaces(value: number): string {
    if (value % 1 === 0) return value.toString()
    return value.toFixed(2)
  }

  //round a number to a specified number
  //if the number is an integer, it will return the number as is
  //if the number is a float, example 1.2345, it will return 1 if 4.9456, it will return 5
  static roundNumber(num: number): number {
    return Number.isInteger(num) ? num : Math.round(num)
  }

  /**
   * Formats a number into a localized currency string using the current language.
   *
   * @param value - The numeric value to format.
   * @param currency - The currency code (e.g., 'USD', 'EUR'). Defaults to EUR.
   * @param currencyDisplay - How the currency should be displayed (symbol, code, name, etc.). Defaults to 'symbol'.
   * @returns A formatted currency string (e.g., "$12.34" or "12,34 €").
   */
  static formatCurrency(
    value: number,
    currency: CurrencyEnum = CurrencyEnum.EUR,
    currencyDisplay: Intl.NumberFormatOptions['currencyDisplay'] = 'narrowSymbol'
  ): string {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency,
      currencyDisplay,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value)
  }

  /**
   * Format a number of bytes to a string with a unit
   * @param bytes The number of bytes to format
   * @returns The formatted number as a string
   * @example
   * ```ts
   * const value = 1000000;
   * const formattedValue = NumberFormatter.formatBytes(value);
   * formattedValue // "1 MB"
   * ```
   */
  static formatBytes(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    const index = Math.floor(Math.log(bytes) / Math.log(1024))
    return (bytes / Math.pow(1024, index)).toFixed(2) + ' ' + units[index]
  }
}
